import { useIsMutating } from "@tanstack/react-query";
import { FiltersV2StorePage } from "PFApp/components/filters_v2/stores/filters_v2_store/filters_v2_store.types";
import { ResourceTableToggle } from "PFApp/components/resource_table_toggle";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Flex, Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useInvalidateManageViewRoles } from "PFCore/hooks/queries/roles/use_invalidate_manage_view_roles";
import { mutationKeys } from "PFCore/hooks/queries/shortlists/mutation_keys";
import { useActivityShortlists } from "PFCore/hooks/queries/shortlists/use_activity_shortlists";
import { Activity } from "PFTypes/activity";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import css from "./resource_table_toggle_container.module.scss";
import { SuggestActionCell } from "./suggest_action_cell";

type ResourceTableToggleContainerProps = {
  activity: Activity;
  canSuggest: boolean;
};

export const ResourceTableToggleContainer = ({ activity, canSuggest }: ResourceTableToggleContainerProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "manageRoles.suggestedWmSidePanel" });
  const { t: commonT } = useTranslation();
  const { showGrowl } = useGrowlStoreActions();
  const { getFirstError } = useResponseErrors();
  const { invalidateAll: invalidateManageViewRoles } = useInvalidateManageViewRoles();

  const {
    data: shortlists,
    isLoading: isShortlistsLoading,
    isFetching: isShortlistsFetching,
    error: shortlistsError,
    isError: isShortlistsError
  } = useActivityShortlists(activity.id);

  useEffect(() => {
    if (shortlistsError) {
      const errorMessage = getFirstError(shortlistsError);
      showGrowl({
        message: errorMessage || commonT("somethingWentWrong"),
        kind: "error"
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortlistsError]);

  const isCreatePending = useIsMutating({ mutationKey: mutationKeys.create() });
  const isUpdatePending = useIsMutating({ mutationKey: mutationKeys.update() });
  const isMutationPending = isCreatePending + isUpdatePending > 0;

  if (isShortlistsError) {
    return null;
  }

  if (isShortlistsLoading) {
    return <LoadingDots circlesEnabled centered />;
  }

  return (
    <Stack gap="spacingLg" className={css.root}>
      {(isShortlistsFetching || isMutationPending) && (
        <Flex className={css.loading} center>
          <LoadingDots circlesEnabled circleSize="xxxxs" />
        </Flex>
      )}
      <Typography variant="bodyRegular">{t("pickWmsBelow")}</Typography>
      <ResourceTableToggle
        pageKey={RecentSearchesPageKey.ManageRolesSuggestedProfiles}
        matchesOptions={{
          activity,
          matchesFiltersStorePage: FiltersV2StorePage.SuggestedWMSidePanelMatches
        }}
        shortlistsOptions={{
          activity,
          shortlistsFiltersStorePage: FiltersV2StorePage.SuggestedWMSidePanelShortlists,
          onShortlistUndo: invalidateManageViewRoles
        }}
        renderActionCell={({ data, option }) => (
          <SuggestActionCell
            data={data}
            option={option}
            activity={activity}
            shortlists={shortlists?.entries ?? []}
            disabled={!canSuggest}
          />
        )}
      />
    </Stack>
  );
};
